import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Search,
  Sparkles,
  Globe,
  Bot,
  Cpu,
  Brain,
} from "lucide-react";
import PageShell from "@/components/site/PageShell";
import FinalCTA from "@/components/site/FinalCTA";

const ENGINES = [
  { icon: Globe, name: "Google", desc: "Classic + Map Pack" },
  { icon: Bot, name: "ChatGPT", desc: "OpenAI citations" },
  { icon: Sparkles, name: "Gemini", desc: "Google AI Overviews" },
  { icon: Brain, name: "Perplexity", desc: "Source attribution" },
  { icon: Cpu, name: "Copilot", desc: "Microsoft + Bing AI" },
];

const PROCESS = [
  {
    n: "01",
    title: "Technical Foundation",
    desc: "Site speed, schema, sitemap, internal linking and Core Web Vitals overhaul.",
  },
  {
    n: "02",
    title: "Content Authority",
    desc: "Topical clusters, expert content and entity-rich articles that earn citations.",
  },
  {
    n: "03",
    title: "Local + GBP",
    desc: "Full Google Business Profile optimization and local citation building.",
  },
  {
    n: "04",
    title: "GEO Engineering",
    desc: "AI-engine-optimized structure so ChatGPT, Gemini & Perplexity cite YOU.",
  },
  {
    n: "05",
    title: "Compounding Reporting",
    desc: "Monthly performance reporting + quarterly strategy sessions.",
  },
];

const INCLUDES = [
  "Full technical SEO audit & roadmap",
  "On-page SEO optimization (every page)",
  "Schema markup (Service, FAQ, Review, LocalBusiness)",
  "Google Business Profile optimization",
  "Local citation building (40+ directories)",
  "Monthly SEO content (2 – 6 articles)",
  "GEO optimization for AI engines",
  "Keyword tracking (15 – 75 keywords)",
  "Monthly performance reporting",
  "Quarterly strategy calls",
];

const FAQS = [
  {
    q: "How long until I see SEO results?",
    a: "Early traction shows in 30 – 60 days. Meaningful ranking shifts and qualified lead growth typically land in months 3 – 6, with compounding results 9+ months in.",
  },
  {
    q: "What is GEO and how is it different from SEO?",
    a: "SEO optimizes for Google's traditional search results. GEO (Generative Engine Optimization) optimizes for AI engines like ChatGPT, Gemini and Perplexity — so they cite your business when buyers ask AI-driven questions.",
  },
  {
    q: "Do you guarantee #1 rankings?",
    a: "No agency can ethically guarantee specific rankings. We guarantee a methodical, proven process and measurable progress month-over-month, with transparent reporting.",
  },
  {
    q: "What industries do you serve?",
    a: "We specialize in home services (roofing, HVAC, plumbing, electricians, construction, landscaping, cleaning), medical practices and ambitious local businesses.",
  },
];

export default function ServiceSeoGeoPage() {
  return (
    <PageShell testId="service-seo-geo-page">
      <section className="relative pt-12 pb-16 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
        <div className="absolute -top-32 right-0 w-[60rem] h-[60rem] radial-glow opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <Link
            to="/services"
            data-testid="service-detail-back"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-cyan-400 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> All Services
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-8 grid lg:grid-cols-12 gap-12 items-center"
          >
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
                <Search className="w-3.5 h-3.5" />
                SEO + GEO Service
              </div>
              <h1 className="mt-6 font-display text-5xl md:text-6xl font-extrabold tracking-tight leading-[1.05]">
                Rank on Google.{" "}
                <span className="text-gradient-brand">
                  Get cited by AI.
                </span>
              </h1>
              <p className="mt-6 text-lg text-white/65 max-w-xl">
                A complete search engine — traditional SEO married with
                Generative Engine Optimization (GEO) — so you win on Google AND
                inside ChatGPT, Gemini, Perplexity and Copilot.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/book-audit"
                  data-testid="service-cta-audit"
                  className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-400 text-black font-semibold px-6 py-3.5 rounded-full hover:shadow-[0_0_30px_rgba(0,229,255,0.4)] transition-all"
                >
                  Get Free SEO Audit
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/pricing"
                  data-testid="service-cta-pricing"
                  className="inline-flex items-center justify-center gap-2 bg-white/5 border border-white/10 text-white font-semibold px-6 py-3.5 rounded-full hover:bg-white/10 transition-all backdrop-blur-md"
                >
                  See SEO Packages
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-6">
                <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold mb-4">
                  Engines We Win On
                </div>
                <div className="space-y-3">
                  {ENGINES.map((e) => (
                    <div
                      key={e.name}
                      className="flex items-center gap-4 p-3 rounded-xl border border-white/10 bg-white/[0.025]"
                    >
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-600/20 to-cyan-400/10 border border-white/10 flex items-center justify-center">
                        <e.icon className="w-4 h-4 text-cyan-400" />
                      </div>
                      <div>
                        <div className="font-semibold">{e.name}</div>
                        <div className="text-xs text-white/55">{e.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="section pt-0" data-testid="service-process">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
              Process
            </div>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-bold tracking-tight">
              How we build SEO that compounds.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {PROCESS.map((p, i) => (
              <motion.div
                key={p.n}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="rounded-2xl border border-white/10 bg-white/[0.025] p-5"
                data-testid={`service-process-${i}`}
              >
                <div className="font-display text-3xl font-extrabold text-white/15">
                  {p.n}
                </div>
                <div className="mt-3 font-display text-lg font-semibold">
                  {p.title}
                </div>
                <p className="mt-1.5 text-sm text-white/55 leading-relaxed">
                  {p.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section pt-0">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-8 md:p-12">
            <div className="grid md:grid-cols-3 gap-10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
                  What&apos;s Included
                </div>
                <h3 className="mt-2 font-display text-3xl font-bold leading-tight">
                  Everything you need to dominate search.
                </h3>
                <p className="mt-4 text-white/55">
                  Every SEO engagement includes the work below, scaled to your
                  package tier.
                </p>
                <Link
                  to="/pricing"
                  className="mt-6 inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-semibold text-sm"
                >
                  Compare packages <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <ul className="md:col-span-2 grid sm:grid-cols-2 gap-3">
                {INCLUDES.map((it) => (
                  <li key={it} className="flex items-start gap-3 text-white/85">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-gradient-to-br from-blue-600 to-cyan-400 flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-black" />
                    </span>
                    <span className="text-sm">{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="section pt-0" data-testid="service-faq">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
              FAQ
            </div>
            <h2 className="mt-5 font-display text-3xl md:text-4xl font-bold tracking-tight">
              SEO &amp; GEO questions, answered.
            </h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <details
                key={f.q}
                data-testid={`service-faq-${i}`}
                className="group rounded-2xl border border-white/10 bg-white/[0.025] open:border-cyan-500/30 transition-colors"
              >
                <summary className="list-none cursor-pointer px-6 py-5 flex items-center justify-between gap-3 font-display text-lg font-semibold">
                  {f.q}
                  <span className="text-cyan-400 transition-transform group-open:rotate-45 text-2xl leading-none">
                    +
                  </span>
                </summary>
                <div className="px-6 pb-5 text-white/65 leading-relaxed">
                  {f.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </PageShell>
  );
}
